import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  LinearProgress,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Stack,
  Divider,
  CardHeader,
  Badge,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AssetIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  Cancel as DisposedIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Assessment as StatisticsIcon,
  AddCircle as CreateIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES, PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import assetsAPI from "../../services/api/assets.api";

const AssetRegisterPage = () => {
  // Hooks
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  // State management
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetSummary, setAssetSummary] = useState({
    totalAssets: 0,
    totalPurchaseValue: 0,
    totalCurrentValue: 0,
    activeAssets: 0,
    maintenanceAssets: 0,
    disposedAssets: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    asset_name: "",
    category: "",
    description: "",
    purchase_date: "",
    purchase_cost: "",
    location: "",
    custodian: "",
    serial_number: "",
    condition: "Good",
    status: "Active",
  });

  // Asset categories and statuses
  const assetCategories = [
    "IT Equipment",
    "Furniture",
    "Vehicle",
    "Office Equipment",
    "Building",
    "Land",
    "Machinery",
    "Tools",
    "Other",
  ];

  const assetStatuses = [
    "Active",
    "Inactive",
    "Under Maintenance",
    "Disposed",
    "Lost",
    "Stolen",
    "Transferred",
  ];

  // Effects
  useEffect(() => {
    fetchAssets();
  }, [pagination.page, pagination.limit]);

  // Data fetching
  const fetchAssets = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
      };

      const response = await assetsAPI.getAll(params);
      const data = response.data?.data || response.data || {};
      setAssets(data.assets || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
      setAssetSummary(data.summary || {
        totalAssets: 0,
        totalPurchaseValue: 0,
        totalCurrentValue: 0,
        activeAssets: 0,
        maintenanceAssets: 0,
        disposedAssets: 0,
      });
    } catch (error) {
      console.error("Error fetching assets:", error);
      showError("Failed to fetch assets");
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleCreateAsset = () => {
    setCreateForm({
      asset_name: "", category: "", description: "",
      purchase_date: "", purchase_cost: "", location: "",
      custodian: "", serial_number: "", condition: "Good", status: "Active",
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.asset_name || !createForm.category || !createForm.purchase_date ||
        !createForm.purchase_cost || !createForm.location || !createForm.custodian) {
      showError("Please fill in all required fields");
      return;
    }
    try {
      setCreateLoading(true);
      await assetsAPI.create(createForm);
      showSuccess("Asset registered successfully");
      setCreateDialogOpen(false);
      fetchAssets();
    } catch (error) {
      showError("Failed to register asset");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditAsset = (assetId) => {
    navigate(`/finance/assets/${assetId}/edit`);
  };

  const handleViewAsset = (assetId) => {
    navigate(`/finance/assets/${assetId}`);
  };

  const handleDeleteAsset = async (assetId) => {
    openDialog({
      title: "Delete Asset",
      message:
        "Are you sure you want to delete this asset? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          await assetsAPI.delete(assetId);
          showSuccess("Asset deleted successfully");
          fetchAssets();
        } catch (error) {
          showError("Failed to delete asset");
        }
      },
    });
  };

  const handleMenuClick = (event, asset) => {
    setAnchorEl(event.currentTarget);
    setSelectedAsset(asset);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAsset(null);
  };

  const handleExport = () => {
    assetsAPI.exportToCSV(
      assets,
      `assets-export-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    showSuccess("Assets exported successfully");
  };

  const handleRefresh = () => {
    fetchAssets();
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchAssets();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setStatusFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "default";
      case "Under Maintenance":
        return "warning";
      case "Disposed":
        return "error";
      case "Lost":
        return "error";
      case "Stolen":
        return "error";
      case "Transferred":
        return "info";
      default:
        return "default";
    }
  };

  // DataGrid columns
  const columns = [
    {
      field: "asset_code",
      headerName: "Asset Code",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "asset_name",
      headerName: "Asset Name",
      width: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.category}
          </Typography>
        </Box>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      width: 150,
    },
    {
      field: "custodian",
      headerName: "Custodian",
      width: 150,
    },
    {
      field: "purchase_cost",
      headerName: "Purchase Cost",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{formatCurrency(params.value)}</Typography>
      ),
    },
    {
      field: "current_value",
      headerName: "Current Value",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{formatCurrency(params.value)}</Typography>
      ),
    },
    {
      field: "condition",
      headerName: "Condition",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "Excellent"
              ? "success"
              : params.value === "Good"
                ? "primary"
                : params.value === "Fair"
                  ? "warning"
                  : "error"
          }
          size="small"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuClick(e, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (loading && assets.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Asset Register
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and track organizational assets
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                  <AssetIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {assetSummary.totalAssets}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Assets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                  <ActiveIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {assetSummary.activeAssets}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Assets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
                  <WarningIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {assetSummary.maintenanceAssets}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Under Maintenance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                  <StatisticsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(assetSummary.totalCurrentValue)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {assetCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {assetStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Assets ({pagination.total})</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<CreateIcon />}
            onClick={handleCreateAsset}
          >
            Add Asset
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Assets Table */}
      <Card>
        <DataGrid
          rows={assets}
          columns={columns}
          pageSize={pagination.limit}
          rowCount={pagination.total}
          loading={loading}
          paginationMode="server"
          onPageChange={handlePageChange}
          disableSelectionOnClick
          autoHeight
          sx={{
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              borderBottom: "2px solid #e0e0e0",
            },
          }}
        />
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create Asset Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Register New Asset</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Asset Name"
                value={createForm.asset_name}
                onChange={(e) => setCreateForm(f => ({ ...f, asset_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={createForm.category} label="Category"
                  onChange={(e) => setCreateForm(f => ({ ...f, category: e.target.value }))}
                >
                  {assetCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Purchase Date" type="date"
                InputLabelProps={{ shrink: true }}
                value={createForm.purchase_date}
                onChange={(e) => setCreateForm(f => ({ ...f, purchase_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Purchase Cost (TZS)" type="number"
                value={createForm.purchase_cost}
                onChange={(e) => setCreateForm(f => ({ ...f, purchase_cost: e.target.value }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Location"
                value={createForm.location}
                onChange={(e) => setCreateForm(f => ({ ...f, location: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Custodian"
                value={createForm.custodian}
                onChange={(e) => setCreateForm(f => ({ ...f, custodian: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Serial Number"
                value={createForm.serial_number}
                onChange={(e) => setCreateForm(f => ({ ...f, serial_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select
                  value={createForm.condition} label="Condition"
                  onChange={(e) => setCreateForm(f => ({ ...f, condition: e.target.value }))}
                >
                  {["Excellent", "Good", "Fair", "Poor"].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={createForm.status} label="Status"
                  onChange={(e) => setCreateForm(f => ({ ...f, status: e.target.value }))}
                >
                  {assetStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={3} label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={createLoading}>
            {createLoading ? "Saving..." : "Register Asset"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleViewAsset(selectedAsset?.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {hasPermission(PERMISSIONS.UPDATE_ASSET) && (
          <MenuItem
            onClick={() => {
              handleEditAsset(selectedAsset?.id);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Asset</ListItemText>
          </MenuItem>
        )}
        {hasPermission(PERMISSIONS.DELETE_ASSET) && (
          <MenuItem
            onClick={() => {
              handleDeleteAsset(selectedAsset?.id);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Asset</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default AssetRegisterPage;
